import { HttpMethod } from "@/lib/utils/http-methods";
import { Store } from "@/stores/common/store.core";
import type { SerializedCachedEquipmentCompanyType } from "@laborchart-modules/common/dist/postgres/serializers/cached-equipment-company-type-serializer";
import type {
   GetEquipmentByTypeIdResponse,
   GetEquipmentDetailsResponse,
} from "@laborchart-modules/lc-core-api/dist/api/equipment/get-equipment";
import type { GetEquipmentTypeResponse } from "@laborchart-modules/lc-core-api/dist/api/equipment-type/get-equipment-type";
import type { StoreJsonResponse, StoreStreamResponse } from "./common";

interface AssociateEquipmentToProjectResponse {
   data: [
      {
         equipment_id: string;
         project_id: string;
      },
   ];
}
export abstract class EquipmentRegistryStore extends Store {
   static getEquipmentDetails(equipmentId: string): StoreJsonResponse<GetEquipmentDetailsResponse> {
      return this.requestJson({
         url: `/api/v3/equipment/${equipmentId}`,
         method: HttpMethod.GET,
      });
   }

   static findEquipmentTypesStream(): StoreStreamResponse<SerializedCachedEquipmentCompanyType> {
      return this.requestStream({
         url: "/api/v3/equipment-types",
         method: HttpMethod.GET,
      });
   }

   static getEquipmentType(equipmentTypeId: string): StoreJsonResponse<GetEquipmentTypeResponse> {
      return this.requestJson({
         url: `/api/v3/equipment-types/${equipmentTypeId}`,
         method: HttpMethod.GET,
      });
   }

   /**
    * Retrieves equipment items by their type ID.
    * @param equipmentTypeId - The equipment type ID
    * @returns Response containing the equipment items
    */
   static getEquipmentByTypeId(
      equipmentTypeId: string,
   ): StoreJsonResponse<GetEquipmentByTypeIdResponse> {
      return this.requestJson({
         url: `/api/v3/equipment?type_id=${equipmentTypeId}`,
         method: HttpMethod.GET,
      });
   }

   /**
    * Associates equipment items with a Procore project.
    * @param procoreCompanyId - The Procore company ID
    * @param projectId - The Procore project ID to associate equipment with
    * @param equipmentIds - Array of equipment IDs to associate
    * @returns Response containing the association results
    */
   static associateEquipmentToProject(
      procoreCompanyId: string,
      projectId: string,
      equipmentIds: string[],
   ): StoreJsonResponse<AssociateEquipmentToProjectResponse> {
      const payload = equipmentIds.map((equipmentId) => ({
         equipment_id: equipmentId,
         project_id: projectId,
      }));
      return this.requestProcoreJson({
         url: `/rest/v2.0/companies/${procoreCompanyId}/equipment_register/associate`,
         method: HttpMethod.POST,
         body: payload,
      });
   }
}
